// 持久化儲存工具 - 支援檔案和資料的localStorage儲存
import { reactive, watch } from 'vue'

// 儲存鍵值
const STORAGE_KEYS = {
    CASE_DATA: 'mountain_search_case_data',
    COMMON_PARAMS: 'mountain_search_common_params',
    ANALYSIS_DATA: 'mountain_search_analysis_data',
    UPLOADED_FILES: 'mountain_search_uploaded_files'
}

// 檔案轉換工具
class FileConverter {
    // 將File物件轉換為可儲存的格式
    static async fileToStorage(file) {
        return new Promise((resolve, reject) => {
            const reader = new FileReader()
            reader.onload = () => {
                resolve({
                    name: file.name,
                    type: file.type,
                    size: file.size,
                    lastModified: file.lastModified,
                    data: reader.result, // base64編碼的資料
                    isFile: true
                })
            }
            reader.onerror = reject
            reader.readAsDataURL(file)
        })
    }

    // 將儲存格式轉換回File物件
    static storageToFile(storageData) {
        if (!storageData.isFile) return null
        
        // 從base64轉換回Blob
        const byteCharacters = atob(storageData.data.split(',')[1])
        const byteNumbers = new Array(byteCharacters.length)
        for (let i = 0; i < byteCharacters.length; i++) {
            byteNumbers[i] = byteCharacters.charCodeAt(i)
        }
        const byteArray = new Uint8Array(byteNumbers)
        const blob = new Blob([byteArray], { type: storageData.type })
        
        // 創建File物件
        return new File([blob], storageData.name, {
            type: storageData.type,
            lastModified: storageData.lastModified
        })
    }
}

// 持久化儲存管理器
class PersistentStorage {
    constructor() {
        this.storage = reactive({
            caseData: this.loadFromStorage(STORAGE_KEYS.CASE_DATA) || {},
            commonParams: this.loadFromStorage(STORAGE_KEYS.COMMON_PARAMS) || {},
            analysisData: this.loadFromStorage(STORAGE_KEYS.ANALYSIS_DATA) || {},
            uploadedFiles: this.loadFromStorage(STORAGE_KEYS.UPLOADED_FILES) || {}
        })

        // 監聽資料變化並自動儲存
        this.setupAutoSave()
    }

    // 從localStorage載入資料
    loadFromStorage(key) {
        try {
            const data = localStorage.getItem(key)
            return data ? JSON.parse(data) : null
        } catch (error) {
            console.warn(`載入${key}失敗:`, error)
            return null
        }
    }

    // 儲存到localStorage
    saveToStorage(key, data) {
        try {
            localStorage.setItem(key, JSON.stringify(data))
            console.log(`已儲存${key}到localStorage`)
        } catch (error) {
            console.error(`儲存${key}失敗:`, error)
        }
    }

    // 設定自動儲存
    setupAutoSave() {
        // 監聽案件資料變化
        watch(() => this.storage.caseData, (newData) => {
            this.saveToStorage(STORAGE_KEYS.CASE_DATA, newData)
        }, { deep: true })

        // 監聽共同參數變化
        watch(() => this.storage.commonParams, (newData) => {
            this.saveToStorage(STORAGE_KEYS.COMMON_PARAMS, newData)
        }, { deep: true })

        // 監聽分析資料變化
        watch(() => this.storage.analysisData, (newData) => {
            this.saveToStorage(STORAGE_KEYS.ANALYSIS_DATA, newData)
        }, { deep: true })

        // 監聽上傳檔案變化
        watch(() => this.storage.uploadedFiles, (newData) => {
            this.saveToStorage(STORAGE_KEYS.UPLOADED_FILES, newData)
        }, { deep: true })
    }

    // 儲存案件資料
    saveCaseData(caseData) {
        this.storage.caseData = { ...caseData }
    }

    // 載入案件資料
    loadCaseData() {
        return { ...this.storage.caseData }
    }

    // 儲存共同參數
    saveCommonParams(params) {
        this.storage.commonParams = { ...params }
    }

    // 載入共同參數
    loadCommonParams() {
        return { ...this.storage.commonParams }
    }

    // 儲存分析模組資料
    saveAnalysisData(moduleName, data) {
        this.storage.analysisData[moduleName] = { ...data }
    }

    // 載入分析模組資料
    loadAnalysisData(moduleName) {
        return this.storage.analysisData[moduleName] || {}
    }

    // 儲存上傳檔案
    async saveUploadedFiles(files, category = 'common') {
        try {
            const filePromises = files.map(file => FileConverter.fileToStorage(file))
            const storedFiles = await Promise.all(filePromises)
            this.storage.uploadedFiles[category] = storedFiles
        } catch (error) {
            console.error('儲存檔案失敗:', error)
        }
    }

    // 載入上傳檔案
    loadUploadedFiles(category = 'common') {
        const storedFiles = this.storage.uploadedFiles[category] || []
        return storedFiles.map(storedFile => FileConverter.storageToFile(storedFile)).filter(Boolean)
    }

    // 清除特定類別的檔案
    clearFiles(category = 'common') {
        this.storage.uploadedFiles[category] = []
    }

    // 清除所有資料
    clearAll() {
        this.storage.caseData = {}
        this.storage.commonParams = {}
        this.storage.analysisData = {}
        this.storage.uploadedFiles = {}
        
        // 清除localStorage
        Object.values(STORAGE_KEYS).forEach(key => {
            localStorage.removeItem(key)
        })
        
        console.log('已清除所有持久化資料')
    }

    // 檢查是否有儲存的資料
    hasStoredData() {
        return Object.values(this.storage).some(data => 
            Object.keys(data).length > 0
        )
    }

    // 獲取儲存狀態
    getStorageStatus() {
        return {
            hasCaseData: Object.keys(this.storage.caseData).length > 0,
            hasCommonParams: Object.keys(this.storage.commonParams).length > 0,
            hasAnalysisData: Object.keys(this.storage.analysisData).length > 0,
            hasUploadedFiles: Object.keys(this.storage.uploadedFiles).length > 0,
            totalSize: this.getStorageSize()
        }
    }

    // 獲取儲存大小
    getStorageSize() {
        try {
            let totalSize = 0
            Object.values(STORAGE_KEYS).forEach(key => {
                const data = localStorage.getItem(key)
                if (data) {
                    totalSize += new Blob([data]).size
                }
            })
            return totalSize
        } catch (error) {
            return 0
        }
    }
}

// 創建全域實例
const persistentStorage = new PersistentStorage()

export default persistentStorage
export { FileConverter, STORAGE_KEYS }
